import { Button, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import React from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { useNotification } from '../../../../Helper/NotificationProvider';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { addMatching } from '../../../../slices/matchingSlice';
import { patchEvent } from '../../../../slices/eventSlice';

const FileUpload = ({ guestList, fetchEvent, currentLocation, fetchData, patchData, event, patcheData, setPatchedData}) => {
  const { openNotification } = useNotification();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const KNOWN_KEYS = [
    'UID',
    'Sitzplatznummer',
    'Reihe',
    'Block',
    'Template',
    'Label-ID',
    'Vorname',
    'Nachname',
    'Anrede/Titel',
    'Firma',
    'Freifeld',
  ];

  const assignImportedData = async (data) => {
    try {
      const allCustomHeaders = new Set();

      data.forEach(row => {
        Object.keys(row).forEach(key => {
          if (!KNOWN_KEYS.includes(key)) {
            allCustomHeaders.add(key);
          }
        });
      });

      const customFields = Array.from(allCustomHeaders);

      const updatedGuestList = guestList.map((guest) => {
        const importedGuest = data.find(
          (item) => item['UID'] === guest.chair
        );
        if (!importedGuest) return guest;

        const customFields = Object.entries(importedGuest)
          .filter(([key]) => !KNOWN_KEYS.includes(key))
          .reduce((acc, [key, value]) => {
            acc[key] = value;
            return acc;
          }, {});

        return {
          ...guest,
          name: importedGuest['Vorname'] || '',
          surname: importedGuest['Nachname'] || '',
          title: importedGuest['Anrede/Titel'] || '',
          companyInfo: importedGuest['Firma'] || '',
          message: importedGuest['Freifeld'] || '',
          matching: {
            matched: !!importedGuest['Label-ID'],
            label: importedGuest['Label-ID'],
            template: importedGuest['Template'],
            customFields
          },
        };
      });

      const newScanPaths = currentLocation.scanPaths.map((scanPath) => ({
          ...scanPath,
          currentMatchingMarker: scanPath.firstMarker
       })
      )

      const updateData = {
        scanPaths: newScanPaths
      }

      const labelsToAdd = data
        .filter((item) => item['Label-ID'])
        .map((item) => {
          const customFieldsForItem = Object.entries(item)
            .filter(([key]) => !KNOWN_KEYS.includes(key))
            .map(([key, value]) => ({
              zoneId: key,
              value,
            }));

          const currentChair = currentLocation.layout.find((chair) => chair.chairId === item['UID'])

          return {
            objectId: item['Label-ID'],
            data: [
              {
                zoneId: 'chair',
                value: item['UID'],
              },
              {
                zoneId: 'number',
                value: currentChair?.shape?.number || '',
              },
              {
                zoneId: 'row',
                value: currentChair?.shape?.row || '',
              },
              {
                zoneId: 'block',
                value: currentChair?.shape?.block || '',
              },
              // {
              //   zoneId: 'number',
              //   value: item['Sitzplatznummer'] || '',
              // },
              // {
              //   zoneId: 'row',
              //   value: item['Reihe'] || '',
              // },
              // {
              //   zoneId: 'block',
              //   value: item['Block'] || '',
              // },
              {
                zoneId: 'template',
                value: item['Template'] || event?.templateName,
              },
              {
                zoneId: 'name',
                value: item['Vorname'] || '',
              },
              {
                zoneId: 'surname',
                value: item['Nachname'] || '',
              },
              {
                zoneId: 'title',
                value: item['Anrede/Titel'] || '',
              },
              {
                zoneId: 'company',
                value: item['Firma'] || '',
              },
              {
                zoneId: 'message',
                value: item['Freifeld'] || '',
              },
              ...customFieldsForItem,
            ],
          };
        });

      if (labelsToAdd.length > 0) {
        await dispatch(addMatching({ labels: labelsToAdd }));
      }
      await patchData(updateData);
      await dispatch(patchEvent({
        eventId: event.key,
        changes: {
          guestList: updatedGuestList,
          customFields
        }
      }))
      fetchEvent()
      openNotification(
        'bottomRight',
        t('Import erfolgreich'),
        t('Die Gästeliste wurde erfolgreich importiert und zugewiesen.'),
        true
      );

      await fetchData();
      setPatchedData(!patcheData);
    } catch (error) {
      openNotification(
        'bottomRight',
        t('Fehler'),
        t('Ein Fehler ist beim Zuweisen der importierten Daten aufgetreten.'),
        false
      );
      console.error(error);
    }
  };


  const validateAndAssignData = (data) => {
    const requiredColumns = ['UID','Label-ID'];
    const errors = [];
    const chairIds = new Set();
    const labelIds = new Set();

    // Check for required columns
    const firstRow = data[0];
    requiredColumns.forEach((col) => {
      if (!firstRow.hasOwnProperty(col)) {
        errors.push(`Fehlende erforderliche Spalte: ${col}`);
      }
    });

    if (errors.length > 0) {
      openNotification(
        'bottomRight',
        t('Importfehler'),
        errors.join('\n'),
        false
      );
      return;
    }

    // Validate each row
    data.forEach((row, index) => {
      const rowNumber = index + 2; // Considering header is row 1
      const chairId = row['UID'];
      const labelId = row['Label-ID'];

      // Validate Chair ID
      if (!chairId || chairId.trim() === '') {
        errors.push(`Zeile ${rowNumber}: Stuhl-ID ist erforderlich.`);
      } else if (chairIds.has(chairId)) {
        errors.push(`Zeile ${rowNumber}: Doppelte Stuhl-ID "${chairId}".`);
      } else {
        chairIds.add(chairId);
      }

      // Validate Label ID if present
      if (labelId && labelId.trim() !== '') {
        if (!/^[0-9a-fA-F]{8}$/.test(labelId)) {
          errors.push(`Zeile ${rowNumber}: Label-ID "${labelId}" ist ungültig. Sie muss genau 8 hexadezimale Zeichen enthalten.`);
        } else if (labelIds.has(labelId)) {
          errors.push(`Zeile ${rowNumber}: Doppelte Label ID "${labelId}".`);
        } else {
          labelIds.add(labelId);
        }
      }
    });

    if (errors.length > 0) {
      openNotification(
        'bottomRight',
        t('Importfehler'),
        errors.join('\n'),
        false
      );
      return;
    }

    assignImportedData(data);
  };


  const handleCSVUpload = (file) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const trimmedData = results.data.map(row => {
          const newRow = {};
          Object.keys(row).forEach(key => {
            newRow[key.trim()] = row[key];
          });
          return newRow;
        });
        validateAndAssignData(trimmedData);
      },
      error: (error) => {
        openNotification(
          'bottomRight',
          t('Fehler'),
          t(`Ein Fehler ist beim Parsen der CSV-Datei aufgetreten: ${error.message}`),
          false
        );
      },
    });
  };

  const handleExcelUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = e.target.result;
      const workbook = XLSX.read(data, { type: 'binary' });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const parsedData = XLSX.utils.sheet_to_json(sheet, { defval: '' });
      const trimmedData = parsedData.map(row => {
        const newRow = {};
        Object.keys(row).forEach(key => {
          newRow[key.trim()] = row[key];
        });
        return newRow;
      });
      validateAndAssignData(trimmedData);
    };
    reader.onerror = (error) => {
      openNotification(
        'bottomRight',
        t('Fehler'),
        t(`Ein Fehler ist beim Lesen der Excel-Datei aufgetreten: ${error.message}`),
        false
      );
    };
    reader.readAsBinaryString(file);
  };


  const handleFileUpload = (file) => {
    const fileExtension = file.name.split('.').pop().toLowerCase();

    if (fileExtension === 'csv') {
      handleCSVUpload(file);
    } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
      handleExcelUpload(file);
    } else {
      openNotification(
        'bottomRight',
        t('Fehler'),
        t('Bitte gültiges CSV oder Excel File hochladen.'),
        false
      );
    }
  };

  return (
    <Upload
      accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
      showUploadList={false}
      beforeUpload={(file) => {
        handleFileUpload(file);
        return false; // Prevent automatic upload
      }}
    >
      <Button icon={<UploadOutlined />}>{t('Gästeliste importieren')}</Button>
    </Upload>
  )
}

export default FileUpload
